import { useEffect, useState } from "react";
import { useToast } from "../../../shared/toastMessages/toastProvider";
import { useNavigate } from "react-router-dom";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import { auth } from "../../../../configs/firebase";

import sessionRequest from "../../../../hooks/authManagement/sessionManagement";
import { clearSessionToken } from "../../../../hooks/authManagement/sessionToken";
import getApiData from "../../../../hooks/apiRequests/getRequest";
import postApiData from "../../../../hooks/apiRequests/postsRequest";

import {
  Typography,
  Button,
  TextField,
  Stack,
  Alert,
  InputAdornment,
  IconButton,
} from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";

import { theme } from "../../../../configs/theme";
import AuthCard, { authButtonSx, authFieldSx } from "../../common/authCard";

const lockedMessage =
  "Account locked. Too many failed login attempts. Please unlock your account.";

const CREDENTIAL_ERROR_CODES = new Set([
  "auth/wrong-password",
  "auth/user-not-found",
  "auth/invalid-credential",
  "auth/invalid-email",
  "auth/invalid-login-credentials",
]);

const networkApiMessage =
  "Unable to reach the login service. Check your connection and try again.";
const networkAuthMessage =
  "Unable to reach authentication. Check your connection and try again.";

const Login = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [accountLocked, setAccountLocked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getApiData(`/HeaderData`);
        if (response.data) {
          navigate("/Portal/Dashboard");
          showToast("User Already Logged In", "success");
        }
      } catch (err) {
        // Not logged in yet — expected on the login page.
        clearSessionToken();
      }
    };

    fetchData();
  }, [navigate, showToast]);

  const showError = (message) => {
    setError(message);
    showToast(message, "error");
  };

  const applyLockState = (payload) => {
    if (!payload) return false;
    if (payload.locked) {
      setAccountLocked(true);
      showError(payload.status || lockedMessage);
      return true;
    }
    setAccountLocked(false);
    return false;
  };

  const recordFailedLogin = async (attemptEmail) => {
    const response = await postApiData("/Login/Failed", { email: attemptEmail });
    if (response?.error && !response?.data) {
      showError(networkApiMessage);
      return;
    }
    const payload = response?.data;
    if (applyLockState(payload)) {
      return;
    }
    showError(payload?.status || "Please enter a correct email and password.");
  };

  const handleLoginFailure = async (err, attemptEmail) => {
    console.error(err);
    clearSessionToken();

    try {
      await signOut(auth);
    } catch (_) {
      /* ignore */
    }

    const code = err?.code || "";
    if (CREDENTIAL_ERROR_CODES.has(code)) {
      await recordFailedLogin(attemptEmail);
      return;
    }

    if (code === "auth/network-request-failed") {
      showError(networkAuthMessage);
      return;
    }

    if (
      code === "session/request-failed" ||
      code === "ERR_NETWORK" ||
      err?.message === "Network Error"
    ) {
      showError(networkApiMessage);
      return;
    }

    if (code === "session/missing-token" || code === "session/empty-response") {
      showError(err.message || networkApiMessage);
      return;
    }

    if (code?.startsWith("auth/")) {
      showError(err.message || "Login failed. Please try again.");
      return;
    }

    if (err?.response?.status) {
      showError(
        err.response.data?.message ||
          err.response.data?.error ||
          networkApiMessage
      );
      return;
    }

    showError(err?.message || networkApiMessage);
  };

  const signInWithEmail = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setError("");
    setAccountLocked(false);
    setSubmitting(true);
    clearSessionToken();

    const trimmedEmail = email.trim();

    try {
      const statusResponse = await postApiData("/Login/Status", {
        email: trimmedEmail,
      });

      if (statusResponse?.error && !statusResponse?.data) {
        showError(networkApiMessage);
        return;
      }

      if (applyLockState(statusResponse?.data)) {
        return;
      }

      const userCredential = await signInWithEmailAndPassword(
        auth,
        trimmedEmail,
        password
      );
      const user = userCredential.user;
      if (!user) {
        await recordFailedLogin(trimmedEmail);
        return;
      }

      const idToken = await user.getIdToken();
      const response = await sessionRequest(user.email, idToken);
      const payload = response?.data;

      if (payload?.locked || payload?.access === false) {
        if (payload?.locked) {
          applyLockState(payload);
          try {
            await signOut(auth);
          } catch (_) {
            /* ignore */
          }
          return;
        }
        showError("Login failed. Please check your email and password.");
        return;
      }

      if (!payload?.token && !payload?.access) {
        showError(networkApiMessage);
        return;
      }

      navigate(`/Portal/Dashboard`);
      showToast("Logged In Successfully.", "success");
    } catch (err) {
      await handleLoginFailure(err, trimmedEmail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthCard
      title="Login"
      subtitle="Enter your email and password to access the sales portal."
      footerTitle="Account issues?"
      footerActions={[
        {
          label: "Reset password",
          onClick: () => navigate("Account/ResetPassword"),
        },
        {
          label: "Resend confirm email",
          onClick: () => navigate("Account/ResendConfirm"),
        },
      ]}
    >
      <Stack component="form" spacing={2} onSubmit={signInWithEmail}>
        <TextField
          id="email"
          label="Email"
          type="email"
          autoComplete="email"
          theme={theme}
          variant="outlined"
          size="small"
          fullWidth
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          sx={authFieldSx}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailOutlinedIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          id="password"
          label="Password"
          type={showPassword ? "text" : "password"}
          autoComplete="current-password"
          theme={theme}
          variant="outlined"
          size="small"
          fullWidth
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          sx={authFieldSx}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlinedIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword((prev) => !prev)}
                  edge="end"
                  size="small"
                >
                  {showPassword ? (
                    <VisibilityOffOutlinedIcon fontSize="small" />
                  ) : (
                    <VisibilityOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {error ? (
          <Alert
            severity={accountLocked ? "warning" : "error"}
            sx={{ borderRadius: "10px" }}
            action={
              accountLocked ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => navigate("Account/Unlock")}
                >
                  Unlock
                </Button>
              ) : null
            }
          >
            {error}
          </Alert>
        ) : null}

        <Button
          type="submit"
          variant="contained"
          theme={theme}
          disabled={submitting}
          fullWidth
          sx={{ ...authButtonSx, mt: 1 }}
        >
          {submitting ? "Signing in..." : "Sign in"}
        </Button>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center" }}
        >
          Need to unlock your account?{" "}
          <Button
            variant="text"
            onClick={() => navigate("Account/Unlock")}
            sx={{
              ...authButtonSx,
              py: 0,
              minWidth: 0,
              verticalAlign: "baseline",
            }}
          >
            Unlock account
          </Button>
        </Typography>
      </Stack>
    </AuthCard>
  );
};

export default Login;
